import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Alert,
  UncontrolledTooltip,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import { Link, useNavigate, useLocation } from "react-router-dom"
import BootstrapTable from "react-bootstrap-table-next"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator"
import {
  formatDate,
  highlightText,
  IsPartnerCockpitModuleEnabled,
} from "helpers/misc_helpers"
import PartFilter from "components/Common/PartFilters"
import Breadcrumbs from "components/Common/Breadcrumb"
import { SearchPartnerCockpitParts } from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import ReactApexChart from "react-apexcharts"

const PartList = props => {
  const location = useLocation()
  const [parts, setParts] = useState([])
  const [filteredParts, setFilteredParts] = useState([])
  const [selectedPartnerCockpit, setSelectedPartnerCockpit] = useState(null)
  const [partnerCockpits, setPartnerCockpits] = useState([])
  const [showResults, setShowResults] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const setLoading = useLoading()
  const [partnerCockpitEnabled, setPartnerCockpitEnabled] = useState(false)
  const isSmallScreen = window.innerWidth < 1600

  const loadPermissions = async _ => {
    setLoading(true)
    const isEnabled = await IsPartnerCockpitModuleEnabled()
    setPartnerCockpitEnabled(isEnabled)
    setLoading(false)
  }

  useEffect(() => {
    loadPermissions()
  }, [])

  // Load search query from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const query = params.get("q")
    if (query) {
      setSearchQuery(query)
      searchParts(query, false)
    }
  }, [location])

  // Convert partner cockpits to select options
  const cockpitOptions = [
    { value: "all", label: "All Partner Cockpits" },
    ...partnerCockpits.map(cockpit => ({
      value: cockpit.id,
      label: cockpit.name,
    })),
  ]

  // Handle partner cockpit filter change
  useEffect(() => {
    if (!selectedPartnerCockpit || selectedPartnerCockpit.value === "all") {
      setFilteredParts(parts)
    } else {
      const filtered = parts.filter(
        part => part.partnerCockpitID === selectedPartnerCockpit.value
      )
      setFilteredParts(filtered)
    }
  }, [selectedPartnerCockpit, parts])

  const searchParts = async (query = searchQuery, updateUrl = true) => {
    if (!query.trim()) return

    setLoading(true)
    try {
      const result = await SearchPartnerCockpitParts(query)
      setParts(result)
      setFilteredParts(result)
      setShowResults(true)
      setError(null)

      // Update URL with search query without reloading
      if (updateUrl) {
        const newUrl = `${location.pathname}?q=${encodeURIComponent(query)}`
        window.history.pushState({}, "", newUrl)
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        setError(
          "You exceeded your daily limit of searches. Please upgrade to premium license!"
        )
      } else {
        setError("An unexpected error happened!")
      }
    }
    setLoading(false)
  }

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      searchParts()
    }
  }

  const options = {
    chart: { sparkline: { enabled: !0 } },
    dataLabels: { enabled: !1 },
    colors: ["#556ee6"],
    plotOptions: {
      radialBar: {
        hollow: { margin: 0, size: "60%" },
        track: { margin: 0 },
        dataLabels: { show: !1 },
      },
    },
  }

  const columns = [
    {
      key: "status",
      dataField: "status",
      text: "Match",
      sort: false,
      headerStyle: _ => {
        return { width: "140px", textAlign: "center" }
      },
      hidden: isSmallScreen,
      formatter: (cell, row) => {
        let percentage = (searchQuery.length / row.partNumber.length) * 100

        return (
          <div className="d-flex justify-content-center align-items-center">
            <ReactApexChart
              options={options}
              series={[percentage]}
              type="radialBar"
              height={30}
              width={30}
              className="apex-charts"
            />
            <span className="ms-2">{percentage.toFixed(0)} %</span>
          </div>
        )
      },
    },
    {
      dataField: "partNumber",
      text: "MPN",
      sort: true,
      formatter: (cell, row) => (
        <div className="d-flex align-items-center">
          <p
            className="mb-0"
            dangerouslySetInnerHTML={highlightText(cell, searchQuery)}
          ></p>
          {row.isRecycled === "1" && (
            <>
              <i
                id={`recycled-${row.id}`}
                className="mdi mdi-recycle text-success ms-2"
                style={{ fontSize: "1.2rem" }}
              />
              <UncontrolledTooltip
                placement="top"
                target={`recycled-${row.id}`}
              >
                This component has been repaired and is ready for reuse.
              </UncontrolledTooltip>
            </>
          )}
          {row.hasCoC === "1" && (
            <>
              <i
                id={`coc-${row.id}`}
                className="mdi mdi-file-certificate text-primary ms-2"
                style={{ fontSize: "1.2rem" }}
              />
              <UncontrolledTooltip placement="top" target={`coc-${row.id}`}>
                This component was repaired by a certified repair company and
                comes with a Certificate of Conformity.
              </UncontrolledTooltip>
            </>
          )}
        </div>
      ),
    },
    {
      dataField: "manufacturer",
      text: props.t("Manufacturer"),
      sort: true,
    },
    {
      dataField: "companyName",
      text: props.t("Company"),
      sort: true,
    },

    {
      dataField: "quantity",
      text: props.t("Quantity"),
      sort: true,
      formatter: cell => `${cell} pc.`,
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "price",
      text: props.t("Price"),
      sort: true,
      formatter: (cell, row) => (
        <div className="blurred-text-container">
          <div>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</div>
        </div>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "leadTime",
      text: props.t("Lead Time"),
      sort: true,
    },
    {
      dataField: "packagingUnit",
      text: props.t("Packaging Unit"),
      sort: true,
    },
    {
      dataField: "partnerCockpitName",
      text: props.t("Partner Cockpit"),
      sort: true,
    },
    {
      dataField: "type",
      text: props.t("Type"),
      sort: true,
      formatter: cell => (
        <span
          className={`badge bg-${
            cell === "loa"
              ? "info"
              : cell === "watchlist"
              ? "warning"
              : "danger"
          } font-size-12`}
        >
          {cell.toUpperCase()}
        </span>
      ),
    },
    {
      dataField: "dateCreated",
      text: props.t("Upload Date"),
      sort: true,
      formatter: cell => formatDate(cell),
    },
    {
      dataField: "actions",
      text: "",
      headerStyle: () => {
        return { width: "220px" }
      },
      formatter: (cell, row) => (
        <div className="d-flex">
          {row.type === "loa" ? (
            <Link
              to={`/partner-cockpit/loa-details/${row.id}`}
              className="btn btn-sm btn-primary ms-2"
            >
              {props.t("View LOA Details")}
            </Link>
          ) : (
            <Link
              to={
                row.type == "part"
                  ? `/part-search/${row.id}`
                  : `/partner-cockpit/demand-details/${row.id}`
              }
              className="btn btn-sm btn-primary ms-2"
            >
              {props.t("View Details")}
            </Link>
          )}
        </div>
      ),
    },
  ]

  const pageOptions = {
    sizePerPage: 10,
    totalSize: filteredParts.length,
    custom: true,
  }

  return (
    <React.Fragment>
      {partnerCockpitEnabled ? (
        <>
          <Breadcrumbs
            title={props.t("Partner Cockpit")}
            breadcrumbItem={props.t("Part List")}
          />

          <section
            className="section hero-section bg-ico-hero"
            id="home"
            style={{
              paddingTop: showResults ? "120px" : "150px",
              paddingBottom: showResults ? "50px" : "100px",
            }}
          >
            <div className="bg-overlay bg-primary"></div>
            <Container>
              <Row className="align-items-center">
                <Col lg={5}>
                  <div className="text-white-50">
                    <h3 className="text-white fw-semibold mb-3">
                      {props.t("Search Part")}
                    </h3>
                    <div className="app-search d-none d-lg-block">
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={props.t(
                            'Search "Manufacturer Part Number"...'
                          )}
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          onKeyPress={handleKeyPress}
                        />
                        <span
                          className="bx bx-search-alt"
                          onClick={() => searchParts()}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>

          {error && (
            <Alert className="mt-4" color="danger">
              {error}
            </Alert>
          )}

          {showResults && (
            <Card>
              <CardBody>
                <PaginationProvider
                  pagination={paginationFactory(pageOptions)}
                  keyField="id"
                  columns={columns}
                  data={filteredParts}
                >
                  {({ paginationProps, paginationTableProps }) => (
                    <ToolkitProvider
                      keyField="id"
                      data={filteredParts}
                      columns={columns}
                      bootstrap4
                      search
                    >
                      {toolkitProps => (
                        <React.Fragment>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">{props.t("Part List")}</h4>
                            <Link
                              to="/parts-upload"
                              className="btn btn-primary"
                            >
                              {props.t("Upload Parts")}
                            </Link>
                          </div>

                          <PartFilter
                            isOpen={true}
                            parts={parts}
                            setParts={setFilteredParts}
                            searchProps={toolkitProps.searchProps}
                            showPartnerCockpitFilter={true}
                            partnerCockpitFilter={{
                              value: selectedPartnerCockpit,
                              onChange: setSelectedPartnerCockpit,
                              options: cockpitOptions,
                            }}
                            isWatchlist
                          />

                          <div className="table-responsive mt-4">
                            <BootstrapTable
                              {...toolkitProps.baseProps}
                              {...paginationTableProps}
                              bordered={false}
                              striped
                              hover
                              classes={"table align-middle table-nowrap"}
                              headerWrapperClasses={"table-light"}
                            />
                            {filteredParts.length === 0 && (
                              <p className="text-center">
                                {props.t("No parts available")}
                              </p>
                            )}
                          </div>
                          <div className="pagination pagination-rounded justify-content-end mb-2">
                            <PaginationListStandalone {...paginationProps} />
                          </div>
                        </React.Fragment>
                      )}
                    </ToolkitProvider>
                  )}
                </PaginationProvider>
              </CardBody>
            </Card>
          )}
        </>
      ) : (
        <Row>
          <Col lg="12">
            <div className="text-center mb-5">
              <h1 className="display-2 fw-medium">
                4<i className="bx bx-buoy bx-spin text-primary display-3" />3
              </h1>
              <h4 className="text-uppercase">
                Sorry, you are not allowed to view this page
              </h4>
              <p className="text-primary">
                This is a premium feature. To use this feature, please upgrade
                your license!
              </p>
              <div className="mt-5 text-center">
                <Link
                  className="btn btn-primary waves-effect waves-light "
                  to="/settings?tab=2"
                >
                  Upgrade Account
                </Link>
                <br />
                <Link className=" d-block mt-4" to="/dashboard">
                  <i className="bx bx-left-arrow-alt" />
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </React.Fragment>
  )
}

export default withTranslation()(PartList)
